import { useMemo } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useSessionStorageValue } from 'src/app/shared/hooks/storage';

import { PERSONALIZED_TASKS_GENERATION_STATUSES } from '../../constants';
import type { UseDynamicTasksReturn } from '../../types';
import { useDynamicCheckboxAction } from '../useDynamicCheckboxAction';
import { useDynamicTaskAction } from '../useDynamicTaskAction';
import { usePollPersonalizedTasks } from '../usePollPersonalizedTasks';

import { DEFAULT_TASKS_DISPLAY_COUNT, DYNAMIC_TASKS_EXPANDED_STORAGE_KEY, getToggleTaskListData } from './model';
import { buildTaskShapes, getDisplayedTasks, getTaskStats, parsePersonalizedTasks } from './taskShapes';

/**
 * Hook that provides dynamic (AI-generated) tasks data.
 */
export function useDynamicTasks(): UseDynamicTasksReturn {
	const { data } = useStarterDashboardV2PageData();
	const initialResult = parsePersonalizedTasks(data.petition.personalizedTasks);

	const shouldPoll =
		initialResult.generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.PENDING ||
		initialResult.generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS;

	const polledData = usePollPersonalizedTasks({ slug: data.petition.slug, shouldPoll });

	const { generationStatus, tasks } = polledData ? parsePersonalizedTasks(polledData) : initialResult;

	const getCtaAction = useDynamicTaskAction();
	const getCheckboxAction = useDynamicCheckboxAction();
	const allTasks = useMemo(
		() =>
			buildTaskShapes(tasks).map((task) => ({
				...task,
				onCtaClick: getCtaAction(task.featureKey, task.id, task.status),
				onCheckboxChange: getCheckboxAction(task.featureKey, task.id),
			})),
		[tasks, getCtaAction, getCheckboxAction],
	);

	const [expandedStorageValue, setExpandedStorageValue] = useSessionStorageValue(DYNAMIC_TASKS_EXPANDED_STORAGE_KEY);
	const taskListIsExpanded = expandedStorageValue === 'true';
	const displayCount = taskListIsExpanded ? allTasks.length : DEFAULT_TASKS_DISPLAY_COUNT;

	const { nextExpandedStorageValue, ...toggleData } = getToggleTaskListData(allTasks, taskListIsExpanded);
	const displayedTasks = useMemo(() => getDisplayedTasks(allTasks, displayCount), [allTasks, displayCount]);
	const stats = useMemo(() => getTaskStats(allTasks), [allTasks]);

	return {
		data: { tasks: displayedTasks, stats, generationStatus, taskListIsExpanded, ...toggleData },
		actions: { toggleTaskList: () => setExpandedStorageValue(nextExpandedStorageValue) },
	};
}
